import React, { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';

const QUOTE_SELECTORS = [
  'div.gmail_quote',
  'div.gmail_extra',
  'div.OutlookMessageHeader',
  'div.moz-cite-prefix',
  'div#appendonsend',
  'div#divRplyFwdMsg',
  'div.yahoo_quoted',
  'div.protonmail_quote',
  'div.zmail_extra',
  'blockquote[type="cite"]',
  'blockquote'
].join(',');

function splitHtmlQuotes(html) {
  if (!html) return { mainHtml: '', quotedHtml: '' };

  const container = document.createElement('div');
  container.innerHTML = html;

  // 1. Buscar por selectores CSS conocidos de clientes de correo
  const quoteEl = container.querySelector(QUOTE_SELECTORS);
  if (quoteEl) {
    const quotedHtml = quoteEl.outerHTML;
    quoteEl.remove();
    // Eliminar tambien hermanos posteriores al bloque de cita (firmas, etc. que iban despues)
    const mainHtml = container.innerHTML.trim();
    return { mainHtml, quotedHtml };
  }

  // 2. Patron Outlook clasico: <hr> seguido de bloque con "De:" / "From:"
  const hrs = container.querySelectorAll('hr');
  for (const hr of hrs) {
    const next = hr.nextElementSibling;
    if (next && /^(De|From|Enviado|Sent)\s*:/i.test(next.textContent.trim())) {
      // Recoger desde el <hr> hasta el final
      const quotedParts = [];
      let node = hr;
      while (node) {
        const nextNode = node.nextSibling;
        quotedParts.push(node.outerHTML || node.textContent);
        node.remove();
        node = nextNode;
      }
      return { mainHtml: container.innerHTML.trim(), quotedHtml: quotedParts.join('') };
    }
  }

  return { mainHtml: html, quotedHtml: '' };
}

// Los enlaces de un email deben abrirse en pestaña nueva y sin filtrar el referrer
// (evita fugas de URL y mitiga phishing por reverse-tabnabbing). Hook global, se
// registra una sola vez al cargar el módulo.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer nofollow');
  }
});

// Neutraliza las imágenes remotas (tracking pixels): guarda el src en data-blocked-src
// y lo retira para que el navegador NO haga la petición hasta que el usuario lo permita.
function processImages(html, show) {
  if (!html) return { html: '', blocked: 0 };
  const div = document.createElement('div');
  div.innerHTML = html;
  let blocked = 0;
  div.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') || img.getAttribute('data-blocked-src') || '';
    const isRemote = /^(https?:)?\/\//i.test(src);
    if (!isRemote) return; // data: o relativas no rastrean
    if (show) {
      const orig = img.getAttribute('data-blocked-src');
      if (orig) { img.setAttribute('src', orig); img.removeAttribute('data-blocked-src'); }
      img.style.display = '';
    } else {
      blocked += 1;
      const cur = img.getAttribute('src');
      if (cur) { img.setAttribute('data-blocked-src', cur); img.removeAttribute('src'); }
      img.removeAttribute('srcset');
      img.style.display = 'none';
    }
  });
  return { html: div.innerHTML, blocked };
}

const EmailBody = ({ text = '' }) => {
  const [showQuotes, setShowQuotes] = useState(false);
  const [showImages, setShowImages] = useState(false);

  const hasHtml = (body) => /<\/?[a-z][\s\S]*>/i.test(body || '');

  const sanitizeHtml = (html) => {
    if (!html) return '';
    let clean = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    clean = clean.replace(/(\r\n|\n){3,}/g, '\n\n');
    return clean.trim();
  };

  const normalizeBody = (body) => {
    if (!body) return '';
    const withoutCR = body.replace(/\r\n/g, '\n');
    return withoutCR.replace(/\n{3,}/g, '\n\n').trim();
  };

  const { main, quoted, isHtml, sanitizedHtml, quotedHtml } = useMemo(() => {
    const raw = text || '';
    const html = hasHtml(raw);
    if (html) {
      const clean = sanitizeHtml(raw);
      const { mainHtml, quotedHtml } = splitHtmlQuotes(clean);
      return { isHtml: true, sanitizedHtml: mainHtml, quotedHtml, main: '', quoted: '' };
    }
    const body = normalizeBody(raw);
    const quoteRegex = /(On .+wrote:|En .+escribió:|-----Original Message-----|-----Mensaje original-----|^From:|^De:|^>)/mi;
    const match = body.match(quoteRegex);
    if (match && typeof match.index === 'number') {
      const idx = match.index;
      return {
        isHtml: false,
        sanitizedHtml: '',
        quotedHtml: '',
        main: body.slice(0, idx).trim(),
        quoted: body.slice(idx).trim()
      };
    }
    return { isHtml: false, sanitizedHtml: '', quotedHtml: '', main: body, quoted: '' };
  }, [text]);

  const { mainDisplay, mainBlocked } = useMemo(() => {
    const r = processImages(sanitizedHtml, showImages);
    return { mainDisplay: r.html, mainBlocked: r.blocked };
  }, [sanitizedHtml, showImages]);
  const quotedDisplay = useMemo(() => processImages(quotedHtml, showImages).html, [quotedHtml, showImages]);

  if (isHtml) {
    return (
      <div className="d-flex flex-column gap-2">
        {mainBlocked > 0 && !showImages && (
          <div className="alert alert-light border py-1 px-2 mb-0 small d-flex justify-content-between align-items-center">
            <span className="text-muted">Se bloquearon {mainBlocked} imagen{mainBlocked === 1 ? '' : 'es'} remota{mainBlocked === 1 ? '' : 's'} por privacidad.</span>
            <button className="btn btn-sm btn-outline-secondary py-0" onClick={() => setShowImages(true)}>
              Mostrar imágenes
            </button>
          </div>
        )}
        <div className="p-2 border rounded" style={{ overflowX: 'auto', maxHeight: '40vh', overflowY: 'auto' }}>
          <div dangerouslySetInnerHTML={{ __html: mainDisplay || '(sin cuerpo)' }} />
        </div>
        {quotedHtml && (
          <div className="small">
            {!showQuotes ? (
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowQuotes(true)}>
                Ver hilo anterior
              </button>
            ) : (
              <>
                <div className="border-start ps-3 text-muted" style={{ overflowX: 'auto' }}
                  dangerouslySetInnerHTML={{ __html: quotedDisplay }} />
                <button className="btn btn-sm btn-outline-secondary mt-2" onClick={() => setShowQuotes(false)}>
                  Ocultar historial
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-2">
      <div className="p-2 border rounded" style={{ whiteSpace: 'pre-wrap', maxHeight: '40vh', overflowY: 'auto' }}>
        {main || '(sin cuerpo)'}
      </div>
      {quoted && (
        <div className="small">
          {!showQuotes ? (
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowQuotes(true)}>
              Ver hilo anterior
            </button>
          ) : (
            <>
              <div className="border-start ps-3 text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                {quoted}
              </div>
              <button className="btn btn-sm btn-outline-secondary mt-2" onClick={() => setShowQuotes(false)}>
                Ocultar historial
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailBody;
